import { TooltipIcon } from '@ipfabric/ui-components/Tooltip/TooltipIcon';
import type { settingTypes } from '@ipfabric/utils';
import { FormikTextAreaField } from 'components/FormikTextAreaField/FormikTextAreaField';
import { FormikTextField } from 'components/FormikTextField/FormikTextField';
import { useLocale } from 'containers/App/LocaleContext';
import { type FormikErrors, type FormikProps, useFormik } from 'formik';
import { type FC, memo } from 'react';
import { validateSlug } from 'utils/formValidation/validations';
import type { FormikValueAdapter } from 'utils/formik/FormikValueAdapter';
import { VendorDialogContainer } from './VendorDialogContainer';
import { FormikAdvancedSettings } from './fields/FormikAdvancedSettings';
import { FormikRateLimitingSettingsFields } from './fields/FormikRateLimitingSettingsFields';
import { FormikVendorApiCommentField } from './fields/FormikVendorApiCommentField';
import { FormikVendorApiEnabledField } from './fields/FormikVendorApiEnabledField';
import { FormikVendorApiRespectSystemProxyConfigurationField } from './fields/FormikVendorApiRejectUnauthorizedField';
import { FormikVendorApiSlugField } from './fields/FormikVendorApiSlugField';
import { getAdvancedSettingsDefaultValues } from './helpers/applyDefaultInitialValues';
import { isVendorUpdate } from './helpers/guardTypes';
import type { BaseVendorFormValues, VendorDialogProps } from './vendorTypes';

export type AzureFormValues =
  BaseVendorFormValues<settingTypes.AzureApiSettings>;

type Props = VendorDialogProps<
  AzureFormValues,
  settingTypes.AzureApiSettings,
  settingTypes.UpdateAzureApiSettings
>;

const resetSecretAttributes = (values: AzureFormValues): AzureFormValues => {
  return {
    ...values,
    clientId: '',
    clientSecret: '',
  };
};

const BareAzureForm: FC<Props> = (props) => {
  const editMode = props.dialogType === 'edit';
  const msg = useLocale();
  const {
    discoverySettings: {
      vendorsApi: { azure: msgAzure },
    },
  } = msg;
  const formik = useFormik<AzureFormValues>({
    initialValues: resetSecretAttributes(props.initialValues),
    validate: (values) => {
      const errors: FormikErrors<AzureFormValues> = {};

      if (!editMode && !values.clientId) {
        errors.clientId = msgAzure.validation.required;
      }
      if (!values.tenantId) {
        errors.tenantId = msgAzure.validation.required;
      }
      if (!editMode && !values.clientSecret) {
        errors.clientSecret = msgAzure.validation.required;
      }
      validateSlug(values, errors, msg);

      return errors;
    },
    onSubmit: (values: AzureFormValues) => {
      const formattedValues = {
        ...values,
        ...getAdvancedSettingsDefaultValues(values),
        subscriptionIds: values.subscriptionIds?.filter(
          (subscriptionId) => subscriptionId.length > 0,
        ),
      };

      if (isVendorUpdate(values) && props.dialogType === 'edit') {
        props.onSubmit({
          ...formattedValues,
          id: values.id,
          clientId:
            values.clientId || props.initialValues.clientId || undefined,
          clientSecret:
            values.clientSecret ||
            props.initialValues.clientSecret ||
            undefined,
        });
      } else if (props.dialogType === 'add') {
        props.onSubmit(formattedValues);
      }
    },
  });

  const stringInputToSubscriptionIds: FormikValueAdapter<
    string,
    settingTypes.AzureApiSettings['subscriptionIds']
  > = {
    toInput: (value) => value?.join('\n') ?? '',
    toModel: (value) => value.split(/\r?\n/),
  };

  return (
    <VendorDialogContainer
      formik={formik as FormikProps<AzureFormValues>}
      dialogType={props.dialogType}
      {...props.vendorDialogContainerPassthruProps}
    >
      <FormikVendorApiEnabledField formik={formik} />
      <FormikTextAreaField
        className='h-24'
        formik={formik}
        label={
          <>
            {msgAzure.subscriptionIds}
            <TooltipIcon
              content={msgAzure.subscriptionIdsTooltip}
              iconProps={{
                iconStyle: 'solid',
                inline: true,
              }}
              size='sm'
              className='ml-2'
            />
          </>
        }
        name='subscriptionIds'
        transformValue={stringInputToSubscriptionIds}
      />
      <FormikTextField
        formik={formik}
        label={msgAzure.tenantId}
        name='tenantId'
        type='text'
      />
      <FormikTextField
        placeholder={editMode ? '(unchanged)' : undefined}
        formik={formik}
        label={msgAzure.clientId}
        name='clientId'
        type='text'
      />
      <FormikTextField
        placeholder={editMode ? '(unchanged)' : undefined}
        formik={formik}
        label={msgAzure.clientSecret}
        name='clientSecret'
        type='password'
      />
      <FormikVendorApiRespectSystemProxyConfigurationField formik={formik} />
      <FormikVendorApiSlugField formik={formik} />
      <FormikVendorApiCommentField formik={formik} />
      <FormikAdvancedSettings>
        <FormikRateLimitingSettingsFields formik={formik} />
      </FormikAdvancedSettings>
    </VendorDialogContainer>
  );
};

export const AzureForm = memo(BareAzureForm);
